/**
 * SummaryModules — Mode-specific comprehension blocks.
 *
 * Each module answers a concrete operator question using synthesized
 * data from the live signal stream. Modules are downstream of signals —
 * they compress and highlight, they don't replace the raw feed.
 *
 * The module registry is keyed by ID. Each mode declares which modules
 * it shows. New modules can be added without touching mode config.
 */

import {
  AlertTriangle, Anchor, BarChart3, Globe2, Cpu, Radio,
  Shield, TrendingDown, Zap, Activity, Ship, Flame, Siren,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { Mode } from '../../hooks/useWorkspaceState';
import type { WorldMonitorSignal } from '../../api/worldMonitor';
import type { SignalSummaryResponse, OsintHealthResponse } from '../../api/osint';
import type { CrossTheatreParadox } from '../../api/crossTheatreParadox';

// ── Module data context ─────────────────────────────────────────────

export interface ModuleData {
  signals: WorldMonitorSignal[];
  summary?: SignalSummaryResponse;
  health?: OsintHealthResponse;
  categoryCounts: Record<string, number>;
  sourceCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  investigationCount: number;
  convergenceCount: number;
  // Paradox data
  localParadoxCount: number;
  localParadoxCriticalCount: number;
  crossTheatreParadoxes: CrossTheatreParadox[];
  // WM composite data (045)
  wmComposites?: import('../../hooks/useWMComposites').WMCompositeData | null;
}

// ── Module definition ───────────────────────────────────────────────

interface ModuleDef {
  id: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  render: (data: ModuleData) => ModuleContent | null;
}

interface ModuleContent {
  metrics: Array<{ label: string; value: string | number; tone?: 'danger' | 'warn' | 'ok' | 'muted' }>;
  detail?: string;
}

// ── Tone → color mapping ────────────────────────────────────────────

function toneColor(tone?: 'danger' | 'warn' | 'ok' | 'muted'): string {
  switch (tone) {
    case 'danger': return 'text-[var(--e-red-400)]';
    case 'warn': return 'text-[var(--e-amber-400)]';
    case 'ok': return 'text-emerald-400';
    case 'muted': return 'text-[var(--e-text-muted)]';
    default: return 'text-[var(--e-text-primary)]';
  }
}

// ── Module registry ─────────────────────────────────────────────────

const MODULES: ModuleDef[] = [
  // ── World mode modules ──────────────────────────────────────────
  {
    id: 'regional_risk',
    title: 'Regional Risk',
    description: 'WM country instability and strategic risk assessment',
    icon: Globe2,
    render: (data) => {
      const wm = data.wmComposites;
      if (!wm?.hasData || !wm.topCII) {
        // Fallback to signal-based view when WM composites unavailable
        const critical = data.signals.filter((s) => s.severity === 'critical');
        const high = data.signals.filter((s) => s.severity === 'high');
        return {
          metrics: [
            { label: 'Critical', value: critical.length, tone: critical.length > 0 ? 'danger' : 'muted' },
            { label: 'High', value: high.length, tone: high.length > 0 ? 'warn' : 'muted' },
          ],
          detail: 'Signal-based view — WM composites loading',
        };
      }
      const ciiTone = wm.topCII.score >= 70 ? 'danger' : wm.topCII.score >= 50 ? 'warn' : 'muted';
      const riskTone = wm.strategicRisk && wm.strategicRisk.score >= 60 ? 'warn' : 'muted';
      return {
        metrics: [
          { label: `CII · ${wm.topCII.country}`, value: wm.topCII.score, tone: ciiTone },
          { label: 'Strategic risk', value: wm.strategicRisk?.score ?? '—', tone: riskTone },
          { label: 'Trend', value: wm.topCII.trend.replace('TREND_DIRECTION_', '').toLowerCase() },
        ],
        detail: wm.strategicRisk
          ? `Strategic risk: ${wm.strategicRisk.level.replace('SEVERITY_LEVEL_', '')} (${wm.strategicRisk.score}/100)`
          : `Top CII: ${wm.topCII.country} at ${wm.topCII.score}/100`,
      };
    },
  },
  {
    id: 'source_health',
    title: 'Feed Status',
    description: 'OSINT collector health and data freshness',
    icon: Radio,
    render: (data) => {
      // Prefer OSINT health endpoint; fall back to WM composites-derived feed health
      const online = data.health?.feeds_online ?? data.wmComposites?.feedsOnline ?? Object.keys(data.sourceCounts).length;
      const total = data.health?.feeds_total ?? data.wmComposites?.feedsTotal ?? 19;
      const latency = data.health?.signal_latency_sec ?? data.wmComposites?.signalLatencySec;
      const sourcesActive = data.wmComposites?.sourcesActive ?? Object.keys(data.sourceCounts).length;
      const ratio = total > 0 ? online / total : 0;

      return {
        metrics: [
          { label: 'Feeds online', value: `${online}/${total}`, tone: ratio >= 0.8 ? 'ok' : ratio >= 0.5 ? 'warn' : 'danger' },
          { label: 'Latency', value: latency != null ? `${latency}s` : '—', tone: latency != null && latency > 120 ? 'warn' : 'muted' },
          { label: 'Sources active', value: sourcesActive },
        ],
      };
    },
  },
  {
    id: 'convergence',
    title: 'Convergence',
    description: 'WM geopolitical pulse and macro regime assessment',
    icon: Activity,
    render: (data) => {
      const wm = data.wmComposites;
      const cells = data.convergenceCount;

      if (!wm?.hasData) {
        // Fallback when WM composites unavailable
        return {
          metrics: [
            { label: 'Cross-domain clusters', value: cells, tone: cells > 5 ? 'warn' : 'muted' },
            { label: 'Visible signals', value: data.signals.length },
            { label: 'Source groups', value: Object.keys(data.categoryCounts).length },
          ],
          detail: 'Signal-based view — WM composites loading',
        };
      }

      const defconTone = (wm.defconLevel ?? 5) <= 2 ? 'danger' : (wm.defconLevel ?? 5) <= 3 ? 'warn' : 'muted';
      return {
        metrics: [
          { label: 'PizzINT DEFCON', value: wm.defconLevel ?? '—', tone: defconTone },
          { label: 'Macro verdict', value: wm.macroVerdict ?? '—', tone: wm.macroVerdict === 'BUY' ? 'ok' : wm.macroVerdict === 'CASH' ? 'warn' : 'muted' },
          { label: 'Tension pairs', value: wm.tensionPairCount },
        ],
        detail: wm.defconLabel
          ? `${wm.defconLabel} · ${wm.macroBullish}/${wm.macroTotal} macro signals bullish`
          : `${cells} convergence cells detected`,
      };
    },
  },

  // ── Finance mode modules ────────────────────────────────────────
  {
    id: 'market_stress',
    title: 'Market Stress',
    description: 'Financial signal intensity and sentiment distribution',
    icon: TrendingDown,
    render: (data) => {
      const financeSignals = data.signals.filter((s) =>
        s.category.includes('finance') || s.category.includes('market') || s.category.includes('alternative'),
      );
      const critical = financeSignals.filter((s) => s.severity === 'critical' || s.severity === 'high');
      const avgConfidence = financeSignals.length > 0
        ? financeSignals.reduce((sum, s) => sum + s.confidence, 0) / financeSignals.length
        : 0;

      if (financeSignals.length === 0) return null;

      return {
        metrics: [
          { label: 'Finance signals', value: financeSignals.length },
          { label: 'Elevated', value: critical.length, tone: critical.length > 2 ? 'danger' : critical.length > 0 ? 'warn' : 'ok' },
          { label: 'Avg confidence', value: `${(avgConfidence * 100).toFixed(0)}%` },
        ],
        detail: critical.length > 0
          ? `${critical.length} elevated finance signals warrant attention`
          : 'Finance signals within normal parameters',
      };
    },
  },
  {
    id: 'sanctions_pressure',
    title: 'Sanctions & Macro',
    description: 'Geopolitical pressure signals affecting financial markets',
    icon: Shield,
    render: (data) => {
      const geoFinance = data.signals.filter((s) =>
        s.category.includes('geopolitical') || s.source.toLowerCase().includes('sanction'),
      );
      const regions = new Set(geoFinance.map((s) => s.region));

      if (geoFinance.length === 0) return null;

      return {
        metrics: [
          { label: 'Pressure signals', value: geoFinance.length },
          { label: 'Regions', value: regions.size },
        ],
        detail: `Cross-domain pressure from ${regions.size} region${regions.size !== 1 ? 's' : ''}`,
      };
    },
  },

  // ── Commodities mode modules ────────────────────────────────────
  {
    id: 'chokepoint_watch',
    title: 'Chokepoint Watch',
    description: 'Maritime chokepoint and trade route disruption signals',
    icon: Anchor,
    render: (data) => {
      const maritime = data.signals.filter((s) =>
        s.category.includes('maritime') || s.category.includes('trade') || s.source.toLowerCase().includes('shipping'),
      );
      const elevated = maritime.filter((s) => s.severity === 'critical' || s.severity === 'high');
      const routes = new Set(maritime.map((s) => s.region));

      if (maritime.length === 0) return null;

      return {
        metrics: [
          { label: 'Maritime signals', value: maritime.length },
          { label: 'Elevated', value: elevated.length, tone: elevated.length > 0 ? 'warn' : 'ok' },
          { label: 'Routes affected', value: routes.size },
        ],
        detail: routes.size > 0
          ? `Activity across ${routes.size} trade route${routes.size !== 1 ? 's' : ''}`
          : undefined,
      };
    },
  },
  {
    id: 'natural_disruption',
    title: 'Natural Events',
    description: 'Earthquake, weather, and environmental disruption signals',
    icon: Flame,
    render: (data) => {
      const natural = data.signals.filter((s) =>
        s.category.includes('natural') || s.category.includes('disaster') || s.category.includes('quake'),
      );
      const severe = natural.filter((s) => s.severity === 'critical' || s.severity === 'high');

      if (natural.length === 0) return null;

      return {
        metrics: [
          { label: 'Natural events', value: natural.length },
          { label: 'Severe', value: severe.length, tone: severe.length > 0 ? 'danger' : 'muted' },
        ],
      };
    },
  },
  {
    id: 'supply_chain',
    title: 'Supply Chain',
    description: 'Shipping volume, commodity flow, and logistics signals',
    icon: Ship,
    render: (data) => {
      const supply = data.signals.filter((s) =>
        s.source.toLowerCase().includes('satellite') ||
        s.source.toLowerCase().includes('shipping') ||
        s.source.toLowerCase().includes('ais'),
      );
      if (supply.length === 0) return null;

      return {
        metrics: [
          { label: 'Supply signals', value: supply.length },
          { label: 'Sources', value: new Set(supply.map((s) => s.source)).size },
        ],
      };
    },
  },

  // ── Tech mode modules ───────────────────────────────────────────
  {
    id: 'cyber_overview',
    title: 'Cyber Threats',
    description: 'Cyber incident, vulnerability, and infrastructure threat signals',
    icon: Zap,
    render: (data) => {
      const cyber = data.signals.filter((s) =>
        s.category.includes('cyber') || s.category.includes('infrastructure'),
      );
      const critical = cyber.filter((s) => s.severity === 'critical');

      if (cyber.length === 0) return null;

      return {
        metrics: [
          { label: 'Cyber signals', value: cyber.length },
          { label: 'Critical', value: critical.length, tone: critical.length > 0 ? 'danger' : 'ok' },
        ],
        detail: critical.length > 0
          ? `${critical.length} critical cyber threat${critical.length !== 1 ? 's' : ''} detected`
          : 'No critical cyber threats',
      };
    },
  },
  {
    id: 'infra_disruption',
    title: 'Infrastructure',
    description: 'Internet outages, platform incidents, and connectivity disruptions',
    icon: Cpu,
    render: (data) => {
      const infra = data.signals.filter((s) =>
        s.source.toLowerCase().includes('internet') ||
        s.source.toLowerCase().includes('outage') ||
        s.category.includes('aviation') ||
        s.source.toLowerCase().includes('cable'),
      );
      if (infra.length === 0) return null;

      return {
        metrics: [
          { label: 'Infra signals', value: infra.length },
          { label: 'Regions', value: new Set(infra.map((s) => s.region)).size },
        ],
      };
    },
  },

  // ── Investigations mode modules ─────────────────────────────────
  {
    id: 'investigation_overview',
    title: 'Research Activity',
    description: 'Active investigations and research status',
    icon: BarChart3,
    render: (data) => {
      return {
        metrics: [
          { label: 'Active investigations', value: data.investigationCount },
          { label: 'Convergence cells', value: data.convergenceCount },
          { label: 'Total signals', value: data.signals.length },
        ],
      };
    },
  },

  // ── Paradox modules (shared across modes) ────────────────────
  {
    id: 'local_paradox',
    title: 'Active Paradoxes',
    description: 'Timeline-level logic breaches with active detonation windows',
    icon: Siren,
    render: (data) => {
      if (data.localParadoxCount === 0) return null;
      return {
        metrics: [
          { label: 'Active', value: data.localParadoxCount, tone: data.localParadoxCount > 0 ? 'warn' : 'muted' },
          { label: 'Critical', value: data.localParadoxCriticalCount, tone: data.localParadoxCriticalCount > 0 ? 'danger' : 'muted' },
        ],
        detail: data.localParadoxCriticalCount > 0
          ? `${data.localParadoxCriticalCount} critical paradox${data.localParadoxCriticalCount !== 1 ? 'es' : ''} with active detonation windows`
          : `${data.localParadoxCount} paradox${data.localParadoxCount !== 1 ? 'es' : ''} under observation`,
      };
    },
  },
  {
    id: 'cross_theatre_paradox',
    title: 'Cross-Theatre Incoherence',
    description: 'Network-level contradictions detected across theatre pairs',
    icon: AlertTriangle,
    render: (data) => {
      const ctps = data.crossTheatreParadoxes;
      if (ctps.length === 0) return null;

      const open = ctps.filter((p) => p.resolution_status === 'OPEN');
      const material = ctps.filter((p) => p.severity === 'MATERIAL' || p.severity === 'CRITICAL');
      const types = new Set(ctps.map((p) => p.paradox_type));

      return {
        metrics: [
          { label: 'Total', value: ctps.length },
          { label: 'Open', value: open.length, tone: open.length > 0 ? 'warn' : 'ok' },
          { label: 'Material+', value: material.length, tone: material.length > 0 ? 'danger' : 'muted' },
          { label: 'Types', value: types.size },
        ],
        detail: open.length > 0
          ? `${open.length} unresolved incoherence${open.length !== 1 ? 's' : ''} across ${new Set(ctps.flatMap((p) => [p.theatre_a_id, p.theatre_b_id])).size} theatres`
          : 'All cross-theatre paradoxes resolved',
      };
    },
  },
];

const MODULE_MAP = new Map(MODULES.map((m) => [m.id, m]));

// ── Mode → module mapping ───────────────────────────────────────────

const MODE_MODULES: Record<Mode, string[]> = {
  world: ['regional_risk', 'local_paradox', 'source_health', 'convergence'],
  finance: ['market_stress', 'sanctions_pressure', 'source_health'],
  commodities: ['chokepoint_watch', 'natural_disruption', 'supply_chain'],
  tech: ['cyber_overview', 'infra_disruption', 'source_health'],
  investigations: ['investigation_overview', 'cross_theatre_paradox', 'source_health'],
};

// ── Render ──────────────────────────────────────────────────────────

function ModuleCard({ def, data }: { def: ModuleDef; data: ModuleData }) {
  const content = def.render(data);
  if (!content) return null;

  const Icon = def.icon;

  return (
    <div className="flex-1 min-w-[14rem] rounded-lg border border-[var(--e-border-secondary)]
      bg-[var(--e-bg-card)] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[var(--e-text-muted)]" />
        <span className="text-xs font-semibold text-[var(--e-text-primary)]">{def.title}</span>
      </div>
      <p className="text-[10px] text-[var(--e-text-muted)] mb-3 leading-relaxed">{def.description}</p>

      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {content.metrics.map((m) => (
          <div key={m.label} className="flex flex-col">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]">
              {m.label}
            </span>
            <span className={cn('text-sm font-semibold font-mono tabular-nums', toneColor(m.tone))}>
              {m.value}
            </span>
          </div>
        ))}
      </div>

      {content.detail && (
        <p className="mt-2 text-[10px] text-[var(--e-text-secondary)] leading-relaxed">
          {content.detail}
        </p>
      )}
    </div>
  );
}

export function SummaryModules({
  activeMode,
  data,
}: {
  activeMode: Mode;
  data: ModuleData;
}) {
  const moduleIds = MODE_MODULES[activeMode] ?? MODE_MODULES.world;
  const modules = moduleIds
    .map((id) => MODULE_MAP.get(id))
    .filter((m): m is ModuleDef => m != null);

  if (modules.length === 0) return null;

  return (
    <div>
      <div className="px-6 py-3">
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]">
          {activeMode === 'world' ? 'Global overview' : `${activeMode.charAt(0).toUpperCase() + activeMode.slice(1)} overview`}
        </span>
      </div>
      <div className="px-6 pb-4 flex flex-wrap gap-3">
        {modules.map((mod) => (
          <ModuleCard key={mod.id} def={mod} data={data} />
        ))}
      </div>
      <Separator />
    </div>
  );
}
